import { useSelector } from "react-redux";
import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
// import { useSnackbar } from "notistack";
import * as XLSX from 'xlsx';

import { setPadData, selectRTFO } from "../../reducers/rtfoSlice";
import { Divider, FormControl, Select, ToggleButtonGroup, ToggleButton, MenuItem, Paper, Typography, Button, IconButton, CircularProgress } from "@mui/material";
import { Analytics, Cancel, Edit, Save, UploadFile } from "@mui/icons-material";

import { RTFOPressureAnalysisChart } from "./RTFOPressureAnalysisChart";
import { RTFOPressureAnalysisGrowthChart } from "./RTFOPressureAnalysisGrowthChart";
import { RTFOPressureAnalysisTable } from "./RTFOPressureAnalysisTable";
import { RTFOPressureAnalysisMap } from "./RTFOPressureAnalysisMap";
import { RTFOPressureAnalysisProbabilityChart } from "./RTFOPressureAnalysisProbabilityChart";
import { RTFOPressureAnalysisXfBarChart } from "./RTFOPressureAnalysisXfBarChart";
import { RTFOFiberStrainChart } from "./RTFOFiberStrainChart";
import { RTFOFiberHeatmapChart } from "./RTFOFiberHeatmapChart";

import { RTFOPressureAnalysisHistogramChart } from "./RTFOPressureAnalysisHistogramChart";
import { stripMonitorUWI } from "./rtfoConfig";
import { useSocket } from "../../context/useSocket";
import { RTFOHistoricMonitorChart } from "./RTFOHistoricMonitorChart";


export const RTFOPressureAnalysis = ({ currentWellName, strainData, setStrainData, monitorData, isLoadingMonitorData, isLoadingStrainData, theme, darkMode, gridView }) => {
	
	// const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { socket } = useSocket();

	const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStageName = currentRTFO?.currentStageName;
	const currentStage = currentRTFO?.stageData?.[currentStageName];
  const stageData = currentRTFO?.stageData?.[currentStageName];

	const [monitorUWI, setMonitorUWI] = useState(stageData?.monitorData ? Object.keys(stageData?.monitorData)?.[0] : "");
	const [eventEditMode, setEventEditMode] = useState(false);
	const [monitorWellEvents, setMonitorWellEvents] = useState(currentRTFO?.monitorEvents || []);
	const [fiberData, setFiberData] = useState(currentRTFO?.fiberData || null);
	const [rawStrainData, setRawStrainData] = useState(null);
	// const [strainData, setStrainData] = useState(null);
  // const isLoadingStrainData = useRef(false);

	const shouldUpdateMonitorWellEvents = useRef(true);

  const monitorWellHasEvents = (monitorWellEvents?.find(e => stripMonitorUWI(e.monitorUWI) === stripMonitorUWI(monitorUWI)) || fiberData?.find(e => stripMonitorUWI(e.monitorName) === stripMonitorUWI(monitorUWI))) ? true : false;

	const updateMonitorWellEvents = useCallback((newEvents) => {
		setMonitorWellEvents(newEvents);
	}, []);

	const updateMonitorWellEventsMap = useCallback((status) => {
		shouldUpdateMonitorWellEvents.current = status;
	}, []);

  const handleUploadStrainFile = async (e) => {
    setStrainData(null);
    
    const selectedFile = e.target.files[0];

    const parsedStrainData = await parseRawStrainCSV(selectedFile, stageData);
    
    setStrainData(parsedStrainData);

    e.target.value = null;
  };

  const handleUploadEventsFile = async (e) => {
    const selectedFile = e.target.files[0];
    const fileType = (selectedFile?.name?.split('.')?.slice(-1)?.[0])?.toLowerCase();

    if (fileType === 'csv') {
      console.log('Parsing CSV');
    } else if (fileType === 'xlsx') {
      console.log('Parsing XLSX');
      const parsedFiberData = await parseExcel(selectedFile);
      setFiberData(parsedFiberData);
      if (parsedFiberData?.length > 0) {
        handleSaveChanges(parsedFiberData);
      };
    };

    e.target.value = null;
  };

  const parseExcel = (file) => {
    return new Promise(resolve => {
      const reader = new FileReader();
  
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          // Assuming you have a single sheet, you can access it like this
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          
          // Convert sheet data to JSON
          const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  
          const headers = (jsonData.splice(0, 1))?.[0];
  
          const parsedFiberData = [];
  
          const channelIndices = {
            eventId: headers.indexOf("Event_id"),
            eventType: headers.indexOf("Event_Type_name"),
            eventTypeId: headers.indexOf("Event_Type_int"),
            eventStartMinutes: headers.indexOf("Event_Start_Stage_Minute"),
            eventMidMinutes: headers.indexOf("Event_Mid_Stage_Minute"),
            eventEndMinutes: headers.indexOf("Event_End_Stage_Minute"),
            obsWellName: headers.indexOf("Obs_Well_Name"),
            treatWellName: headers.indexOf("Treat_Well_Name"),
            topDepth: headers.indexOf("Event_Top_MD"),
            centerDepth: headers.indexOf("Event_Center_MD"),
            bottomDepth: headers.indexOf("Event_Bottom_MD"),
            topLat: headers.indexOf("Event_Top_Lat"),
            topLon: headers.indexOf("Event_Top_Long"),
            centerLat: headers.indexOf("Event_Center_Lat"),
            centerLon: headers.indexOf("Event_Center_Long"),
            bottomLat: headers.indexOf("Event_Bottom_Lat"),
            bottomLon: headers.indexOf("Event_Bottom_Long"),
            stageNumber: headers.indexOf("Stage_Number"),
            rate: headers.indexOf("Event_Mid_Rate"),
            pressure: headers.indexOf("Event_Mid_Pressure"),
            proppant: headers.indexOf("Event_Mid_Proppant_Conc"),
            slurryVolume: headers.indexOf("Event_Mid_Stage_Volume"), // volume at the mid
            vfr: headers.indexOf("Event_Start_Stage_Volume"), // volume at the start
            cumProppant: headers.indexOf("Event_Mid_Stage_Proppant"),
            distance: headers.indexOf("Stage_To_Event_Distance_3D"),
            strainJSON: headers.indexOf("Event_Strain_json")
          };
  
          // for (const event of jsonData) {
          for (let i = 0; i < jsonData.length; i++) {
            const event = jsonData[i];
            let wellName = event[channelIndices['treatWellName']];
            if (wellName?.charAt(0)?.toLowerCase() === 'a') {
              wellName = 'Alpha';
            } else if (wellName?.charAt(0)?.toLowerCase() === 'b') {
              wellName = 'Bravo';
            } else if (wellName?.charAt(0)?.toLowerCase() === 'c') {
              wellName = 'Charlie';
            } else if (wellName?.charAt(0)?.toLowerCase() === 'd') {
              wellName = 'Delta';
            };


            if (!wellName) continue;

            let monitorName = event[channelIndices['obsWellName']];
            monitorName = (monitorName?.split(' ')?.[1])?.substring(0, 9);

            const stageNumber = event[channelIndices['stageNumber']];
            const stageName = `${currentRTFO?.name}_${wellName}_${stageNumber}`;
            const stageStartTimestamp = currentRTFO?.stageData?.[stageName]?.stageStartTimestamp > 0 ? currentRTFO?.stageData?.[stageName]?.stageStartTimestamp : (Date.now() + i * 60 * 60 * 1000);
            const eventTimestamp = stageStartTimestamp + +event[channelIndices['eventMidMinutes']] * 60 * 1000;
            const totalSlurryVolume = currentRTFO?.stageData?.[stageName]?.metrics?.totalSlurryVolume || 0;
            const slurryVolume = +event[channelIndices['slurryVolume']] || totalSlurryVolume;
            const vfr = +event[channelIndices['vfr']] || totalSlurryVolume;
            const distance = event[channelIndices['distance']];
            const diffusivity = (Math.pow(distance, 2) / (4 * Math.PI * vfr)) || 0;
            const xf = Math.sqrt(4 * Math.PI * vfr * diffusivity) || 0;
            // const diffusivity = Math.pow(distance, 2) / (4 * Math.PI * cleanVolume);
            // const xf = Math.sqrt(4 * Math.PI * totalCleanVolume * diffusivity) || 0;

            const parsedEventData = {
              eventId: event[channelIndices['eventId']],
              name: `Fiber Event ${event[channelIndices['eventId']]}`,
              eventType: event[channelIndices['eventType']],
              eventTypeId: event[channelIndices['eventTypeId']],
              date: new Date(eventTimestamp)?.toLocaleString(),
              timestamp: eventTimestamp,
              deltaTime: event[channelIndices['eventMidMinutes']],
              monitorName,
              wellName,
              stageName,
              stageNumber,
              topDepth: event[channelIndices['topDepth']],
              centerDepth: event[channelIndices['centerDepth']],
              bottomDepth: event[channelIndices['bottomDepth']],
              topLatLon: [event[channelIndices['topLat']], event[channelIndices['topLon']]],
              centerLatLon: [event[channelIndices['centerLat']], event[channelIndices['centerLon']]],
              bottomLatLon: [event[channelIndices['bottomLat']], event[channelIndices['bottomLon']]],
              pressure: event[channelIndices['pressure']],
              rate: event[channelIndices['rate']],
              proppant: event[channelIndices['proppant']],
              slurryVolume,
              vfr,
              cumProppant: event[channelIndices['cumProppant']],
              diffusivity,
              distance,
              xf
            }

            const strainValuesString = (event[channelIndices['strainJSON']])?.replaceAll('"', '')?.replaceAll("'", "\"");

            const strainValues = JSON.parse(strainValuesString);

            parsedEventData.strainValues = {
              depths: strainValues['Depth'],
              negativeValues: strainValues['Negative_Strain'],
              positiveValues: strainValues['Positive_Strain'],
            }
  
            parsedFiberData.push(parsedEventData);
          }

          // let diffusivities = (parsedFiberData?.map(e => e.diffusivity));
          // diffusivities = diffusivities?.sort((a, b) => a - b);

          // const midPoint = Math.floor(diffusivities?.length / 2);

          // const avgDiffusivitiesLow = diffusivities.slice(0, midPoint);
          // const avgLow = avgDiffusivitiesLow?.reduce((tot, curr) => tot + curr, 0) / avgDiffusivitiesLow?.length;
          // const avgHigh = Math.max(...diffusivities);

          // const vfrArray = (fiberData?.map(e => e.vfr));
          // const avgVFR = vfrArray?.reduce((tot, curr) => tot + curr, 0) / vfrArray?.length;
          // setAvgVFR(avgVFR);

          // for (const event of parsedFiberData) {
          //   event.xfLow = Math.sqrt(4 * Math.PI * event.vfr * avgLow);
          //   event.xfHigh = Math.sqrt(4 * Math.PI * event.vfr * avgHigh);
          // };
  
          resolve(parsedFiberData);

        } catch(error) {
          console.log(error);
          resolve([]);
        }
      };
  
      reader.readAsBinaryString(file);
    })

    
  };

  const parseRawStrainCSV = (file, stageData) => {
    return new Promise(resolve => {
      const reader = new FileReader();
  
      reader.onload = (e) => {
        try {
          let rawString = e.target.result;
          rawString = rawString.replace('Fiber depth (m),', '');

          let data = rawString?.split(/\r?\n/);

          const stageStartTimestamp = stageData?.stageStartTimestamp;
          const stageEndTimestamp = stageData?.stageEndTimestamp + 40 * 60 * 1000; // add 40 min buffer
          
          let rawDates = (data.splice(0, 1)?.[0]?.split(','))?.map(date => `${date}Z`);
          let dates = (data.splice(0, 1)?.[0]?.split(','))?.map(date => `${date}Z`);

          const stageStartIndex = dates.findLastIndex(d => new Date(d)?.getTime() < new Date(stageStartTimestamp)?.getTime());
          const stageEndIndex = dates.findIndex(d => new Date(d)?.getTime() > new Date(stageEndTimestamp)?.getTime());

          dates = dates.slice(stageStartIndex, stageEndIndex);

          const rawStrainData = [];
          const stageStrainData = [];
          const depths = [];

          data.forEach((row,i) => {
            const depthArray = row?.split(',')?.map(el => +el);
            const depth = (depthArray?.splice(0, 1))?.[0];
            if (depth > 0) {
              data[i] = depthArray;
              depths.push(depth);
              rawStrainData.push(depthArray);
              stageStrainData.push(depthArray.slice(stageStartIndex, stageEndIndex));
            }
          })

          setRawStrainData({
            dates: rawDates,
            depths,
            strainData: rawStrainData
          })

          resolve({
            dates,
            depths,
            strainData: stageStrainData
          })
          
        } catch(error) {
          console.log(error);
          resolve([]);
        }
      };
  
      reader.readAsBinaryString(file);
    })

    
  };

	return (
		<Paper elevation={0} sx={{boxShadow: 1}} style={{height: 'auto', padding: 0, margin: '10px 20px', display: 'flex', flexDirection: 'column', backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')])}}>

			<div style={{width: 'calc(100% - 20px)', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', margin: '20px 20px 0px 20px'}}>

				<Typography variant="h6">
					FDI Analysis
				</Typography>

        <div style={{display: 'flex', flexDirection: 'column'}}>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <Button
              component="label"
              variant="outlined" 
              color="primary"
              title="Upload raw strain .csv"
              startIcon={<UploadFile />}
              style={{marginRight: 20, height: 28}}
              size="small"
              disabled
            >
              Upload Strain Data
              <input
                type="file"
                accept=".csv, .CSV"
                hidden
                onChange={handleUploadStrainFile}
              />
            </Button>
            <Button
              component="label"
              variant="outlined" 
              color="primary"
              title="Upload PBI summary .xlsx"
              startIcon={<UploadFile />}
              style={{marginRight: 20, height: 28}}
              size="small"
              disabled
            >
              Upload Events Data
              <input
                type="file"
                accept=".xlsx"
                hidden
                onChange={handleUploadEventsFile}
              />
            </Button>

            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 20}}>
              <Typography fontSize={12} style={{zIndex: 420, marginRight: 10}}>Observation Well:</Typography>
              <FormControl>
                <Select 
                  value={monitorUWI}
                  label="Observation Well"
                  onChange={(e) => setMonitorUWI(e.target.value)}
                  size="small"
                  variant="standard"
                  style={{width: 150, height: 28, zIndex: 69, fontSize: '12px'}}
                >
                  {(currentStage?.monitorData ? Object.keys(currentStage?.monitorData) : []).map((monitorWell) => {
                    return (
                      <MenuItem value={monitorWell} key={monitorWell}>{monitorWell}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </div>
          </div>
          {rawStrainData && <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            <IconButton size="small" onClick={() => {
              setRawStrainData(null);
              setStrainData(null);
            }}><Cancel style={{fontSize: 12}} /></IconButton>
            <Typography variant="caption">{new Date(rawStrainData?.dates?.[0])?.toLocaleString()} - {new Date(rawStrainData?.dates?.slice(-1)?.[0])?.toLocaleString()}</Typography>
          </div>} 
        </div>
			</div>

      <div style={{ display: 'flex', flexDirection: 'row', height: 500 }}>
        <RTFOHistoricMonitorChart monitorData={monitorData} isLoadingMonitorData={isLoadingMonitorData} setMonitorUWI={setMonitorUWI} />
      </div>

			<div style={{display: 'flex', flexDirection: gridView ? 'row' : 'column', marginTop: 40}}>
				<div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', position: 'relative', outline: eventEditMode && '5px solid #f44336', borderRight: gridView && '1px solid lightgrey', marginTop: 0, zIndex: 1001 }}>
					<div 
						style={{
							zIndex: 69, 
							position: 'absolute',
							width: 200,
							left: '50%', 
							top: -35, 
							transform: 'translateX(-50%)', 
							display: 'flex', 
							flexDirection: 'column', 
							alignItems: 'center',
							backgroundColor: eventEditMode ? '#f44336' : '',
							borderRadius: '10px 10px 0 0'
						}}
					>
						{eventEditMode ? <Typography variant="h6" color="#fff">EDIT MODE</Typography> : ''}
					</div>
					{eventEditMode && <div 
						style={{
							zIndex: 999, 
							position: 'absolute', 
							left: '50%', 
							bottom: 0, 
							transform: 'translateX(-50%)', 
							display: 'flex', 
							flexDirection: 'column', 
							alignItems: 'center',
							width: '50%'
						}}
					>
						<Typography variant="body2" color="#f44336">Click to add or remove events</Typography>
					</div>}

					<div style={{ position: 'absolute', top: -30, left: 10, zIndex: 69}}>
						<ToggleButtonGroup
							value={eventEditMode}
							size="small"
							style={{height: 24}}
							onChange={(e, newVal) => setEventEditMode(newVal)}
							exclusive
						>
							<ToggleButton value={true} size="small" title="Edit Events" color={(darkMode ? "" : "primary")}>
								<Edit style={{fontSize: '16px'}} />
							</ToggleButton>
						</ToggleButtonGroup>
					</div>

					{eventEditMode && 
					<ToggleButtonGroup
						value={''}
						size="small"
						style={{height: 24, position: 'absolute', top: -30, right: 10, zIndex: 69}}
						onChange={() => {}}
					>
						<ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges} >
							<Cancel style={{fontSize: '16px'}} />
						</ToggleButton>
						<ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={() => handleSaveChanges(fiberData)}>
							<Save style={{fontSize: '16px'}} />
						</ToggleButton>
					</ToggleButtonGroup>}

          <RTFOPressureAnalysisChart 
            gridView={gridView} 
            stageData={stageData} 
            currentRTFO={currentRTFO} 
            monitorUWI={monitorUWI} 
            monitorWellEvents={monitorWellEvents} 
            fiberData={fiberData}
            updateMonitorWellEvents={updateMonitorWellEvents} 
            eventEditMode={eventEditMode} 
            showSpikeLines={true}
            updateMonitorWellEventsMap={updateMonitorWellEventsMap}
          />
				</div>

				{!gridView && <Divider />}

				<div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', marginTop: gridView ? 0 : 20}}>
					{monitorWellHasEvents ?
          <RTFOPressureAnalysisGrowthChart 
						gridView={gridView} 
            monitorUWI={monitorUWI}
						currentRTFO={currentRTFO} 
            monitorWellEvents={monitorWellEvents}
            fiberData={fiberData}
						showSpikeLines={true}
						theme={theme}
						darkMode={darkMode}
						showLegend={false}
					/> :
          <div style={{
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
            // position: 'absolute', 
            // top: '50%', 
            // left: '50%', 
            // transform: 'translate(-200px, -100px)'
          }}>
            <Analytics style={{fontSize: '72px', color: 'grey'}} />
            <Typography variant="body2" style={{color: 'grey'}}>No events to display</Typography>
          </div>
        }
				</div>
			</div>

			<Divider />
      
      <div style={{width: '100%', display: 'flex', flexDirection: gridView ? 'row' : 'column'}}>
        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 540 : 980}}>
          <RTFOPressureAnalysisMap 
            currentWellName={currentWellName}
            monitorUWI={monitorUWI} 
            monitorWellEvents={monitorWellEvents} 
            fiberData={fiberData}
            updateMonitorWellEvents={updateMonitorWellEvents}
            updateMonitorWellEventsMap={updateMonitorWellEventsMap}
            shouldUpdateMonitorWellEvents={shouldUpdateMonitorWellEvents}
            gridView={gridView}
          />
        </div>
        
        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 540 : 980}}>
          <RTFOPressureAnalysisHistogramChart 
            currentRTFO={currentRTFO}
            monitorWellEvents={monitorWellEvents} 
            fiberData={fiberData}
            monitorUWI={monitorUWI} 
            theme={theme}
            darkMode={darkMode}
            gridView={gridView}
          />
        </div>
      </div>

			<Divider />

      {fiberData?.length > 0 && 
      <>
        <div style={{width: '100%', position: 'relative', display: 'flex', flexDirection: gridView ? 'row' : 'column', marginTop: 40}}>
          <div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', marginTop: gridView ? 0 : 20, borderRight: gridView && '1px solid lightgrey'}}>
            <RTFOFiberStrainChart 
              gridView={gridView} 
              fiberData={fiberData} 
              currentRTFO={currentRTFO} 
              monitorUWI={monitorUWI} 
              showSpikeLines={true}
            />
          </div>

          {!gridView && <Divider />}

          <div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', marginTop: gridView ? 0 : 20}}>
            {
              isLoadingStrainData ? 
              <div style={{height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                <Typography style={{marginBottom: 20}}>Downloading strain data...</Typography>
                <CircularProgress />
              </div> :
              <RTFOFiberHeatmapChart 
              gridView={gridView} 
              strainData={strainData} 
              currentRTFO={currentRTFO} 
              monitorUWI={fiberData?.[0]?.monitorName || ''} 
              showSpikeLines={true}
            />}
          </div>
        </div>

        <Divider />
      </>}

			<div style={{width: '100%', position: 'relative', display: 'flex', flexDirection: gridView ? 'row' : 'column', marginTop: 40}}>
				<div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', borderRight: gridView && '1px solid lightgrey'}}>
					<RTFOPressureAnalysisProbabilityChart 
						currentRTFO={currentRTFO}
						monitorWellEvents={monitorWellEvents} 
            fiberData={fiberData}
						gridView={gridView} 
						showSpikeLines={true}
						theme={theme}
						darkMode={darkMode}
						showLegend={false}
					/>
				</div>

				{!gridView && <Divider />}

				<div style={{height: gridView ? 540 : 980, width: gridView ? '50%' : '100%', marginTop: gridView ? 0 : 20}}>
					<RTFOPressureAnalysisXfBarChart 
						currentRTFO={currentRTFO}
						monitorWellEvents={monitorWellEvents} 
            fiberData={fiberData}
						monitorUWI={monitorUWI}
						gridView={gridView} 
						showSpikeLines={true}
						theme={theme}
						darkMode={darkMode}
						showLegend={false}
					/>
				</div>
			</div>
		
			<Divider />

			

			<div style={{height: 540, width: '100%'}}>
				<RTFOPressureAnalysisTable currentRTFO={currentRTFO} monitorWellEvents={monitorWellEvents} fiberData={fiberData} currentStageName={currentStage?.name} monitorUWI={monitorUWI} />
			</div>	
		
		</Paper>
	);

	// FUNCTIONS
	function handleCancelChanges() {
    try {
      setMonitorWellEvents(currentRTFO?.monitorEvents || []);
			setEventEditMode(false);

    } catch (error) {
      console.log(error);
      return;
    };
  };

	async function handleSaveChanges(fiberData) {
    try {
      
			const newPad = {...currentRTFO, monitorEvents: monitorWellEvents};
      if (fiberData) newPad.fiberData = fiberData;
			delete newPad?.stageData;

      socket?.send(JSON.stringify({
        messageType: 'savePad',
        pad: newPad,
      }));

      newPad.stageData = currentRTFO?.stageData;
      dispatch(setPadData(newPad));

			// const ENV = process.env.REACT_APP_NODE_ENV;
      // const BASE_URL_SSD = ENV === 'production' ? process.env.REACT_APP_BASE_URL_SSD : (ENV === 'staging' || ENV === 'development' ) ? process.env.REACT_APP_BASE_URL_SSD_STAGING : 'http://localhost:8080/api';

      // const options = {
      //   method: 'PUT',
      //   headers: {
      //     'x-api-key': process.env.REACT_APP_API_KEY,
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({name: currentRTFO?.name, payload: { monitorEvents: monitorWellEvents }})
      //   // body: JSON.stringify({rtfo: newPad})
      // };
  
      // let result = await fetch(`${BASE_URL_SSD}/rtfo`, options);

      // // newPad.stageData = currentRTFO?.stageData;

      // if (result?.status === 200) {
      //   console.log('Pad updated successfully');
      //   enqueueSnackbar(`Pad ${newPad?.name} updated successfully`, { variant: 'success' });
      //   dispatch(setPadData(newPad));
      // };



			setEventEditMode(false);


    } catch (error) {
      console.log(error);
      return;
    };
  };
};